//#region IMPORTS
import {
  InstoreTemplate,
  InstoreTemplateRequest,
} from "../../models/instoreTemplate";
import {
  instoreTypesSelectors,
  fetchInstoreTypesAsync,
} from "../../features/instoreTypes/instoreTypesSlice";
import {
  instoreOrderTypesSelectors,
  fetchInstoreOrderTypesAsync,
} from "../../features/instoreOrderTypes/instoreOrderTypesSlice";
import {
  fetchInstoreTemplateAsync,
  instoreTemplatesSelectors,
  createInstoreTemplateAsync,
  updateInstoreTemplateAsync,
} from "../../features/instoreTemplates/instoreTemplatesSlice";
import { parse, isValid as isValidDate, format } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { FieldValues, useForm } from "react-hook-form";
import { InstoreType } from "../../models/instoreType";
import { useEffect } from "react";
import { toast } from "react-toastify";
import SubmitButton from "../genericButtons/submitButton/SubmitButton";
import classNames from "../../helpers/classNames";
import { InstoreOrderType } from "../../models/instoreOrderType";
//#endregion

//#region INTERFACE
interface Props {
  instoreTemplateId?: number;
  cancelCUD: () => void;
  disabled?: boolean;
  create?: boolean;
  isCopy?: boolean;
}
//#endregion

export default function InstoreTemplateForm({
  instoreTemplateId,
  cancelCUD,
  disabled = false,
  create = false,
  isCopy = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { instoreTypes_Loaded, loading: instoreTypesLoading } = useAppSelector(
    ({ instoreTypes }) => instoreTypes
  );
  const instoreTypes = useAppSelector(instoreTypesSelectors.selectAll);

  const { instoreOrderTypes_Loaded, loading: instoreOrderTypesLoading } =
    useAppSelector(({ instoreOrderTypes }) => instoreOrderTypes);
  const instoreOrderTypes = useAppSelector(
    instoreOrderTypesSelectors.selectAll
  );

  const instoreTemplate = useAppSelector((state) =>
    instoreTemplateId != null
      ? instoreTemplatesSelectors.selectById(state, instoreTemplateId)
      : undefined
  );

  // UseForm
  const {
    reset,
    setValue,
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm<InstoreTemplate>({
    mode: "all",
    defaultValues: { active: true },
  });
  //#endregion

  //#region LOGIC
  // Fetch template data
  useEffect(() => {
    const fetchData = async (id: number) => {
      await dispatch(fetchInstoreTemplateAsync(id));
    };
    instoreTemplateId && !create && fetchData(instoreTemplateId);
  }, [instoreTemplateId, create, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchInstoreTypesAsync());
    };
    !instoreTypes_Loaded && fetchData();
  }, [instoreTypes_Loaded, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchInstoreOrderTypesAsync());
    };
    !instoreOrderTypes_Loaded && fetchData();
  }, [instoreOrderTypes_Loaded, dispatch]);

  useEffect(() => {
    if (instoreTemplate) {
      reset(instoreTemplate);

      setValue(
        "order_type_instore_name",
        instoreTemplate.order_type_instore_name
      );

      if (instoreTemplate.from) {
        const fromDate = parse(
          instoreTemplate.from,
          "dd/MM/yyyy HH:mm",
          new Date()
        );
        if (isValidDate(fromDate)) {
          setValue("from", format(fromDate, "yyyy-MM-dd"));
        }
      }

      if (instoreTemplate.until) {
        const untilDate = parse(
          instoreTemplate.until,
          "dd/MM/yyyy HH:mm",
          new Date()
        );
        if (isValidDate(untilDate)) {
          setValue("until", format(untilDate, "yyyy-MM-dd"));
        }
      }
    }
  }, [instoreTemplate, setValue, reset]);

  // Submit form
  async function submitForm(data: FieldValues) {
    const selectedType = instoreTypes.find(
      (instoreType) => instoreType.name === data.instore_type
    );

    if (!selectedType) {
      toast.error("Selected instore type not found");
      return;
    }

    const selectedOrderType = instoreOrderTypes.find(
      (iOt) => iOt.name === data.order_type_instore_name
    );

    if (!selectedOrderType) {
      toast.error("Selected instore type not found");
      return;
    }

    const request: InstoreTemplateRequest = {
      custom_template_id: data.custom_template_id,
      instore_type_id: selectedType.id,
      order_type_instore_id: selectedOrderType.id,
      active: data.active,
      from: data.from,
      until: data.until,
      template_rows: create
        ? selectedType.store_formats.map((sf, i) => ({
            theme_id: 1,
            theme: "Default",
            article_name: "My first article",
            store_format_id: sf.id,
            store_format: sf.name,
            amount: 1,
            index: 9000 + i,
          }))
        : data.template_rows,
    };

    // Creating/copying a template can take a while (large payload). Show a
    // loading toast for the duration; the final success/error toast is emitted
    // by the slice, so we only dismiss the loader here to avoid duplicates.
    const savingToastId = toast.loading(
      "Saving template… This may take a while.",
    );
    try {
      if (instoreTemplate && !isCopy) {
        await dispatch(
          updateInstoreTemplateAsync({
            id: instoreTemplate.id,
            values: request,
          })
        );
      } else {
        await dispatch(
          createInstoreTemplateAsync({
            values: request,
          })
        );
      }
      cancelCUD();
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      toast.dismiss(savingToastId);
    }
  }
  //#endregion

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className="space-y-12">
        <div className="pb-12">
          {/* HEADER */}
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            {instoreTemplate
              ? `${isCopy ? "Copy template" : "Update template:"} ${
                  instoreTemplate.name
                }`
              : "Create new template"}
          </h2>

          {/* BODY */}
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* custom_template_id */}
            <fieldset className="sm:col-span-3 hidden">
              <label
                htmlFor="custom_template_id"
                className={
                  errors.custom_template_id
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.custom_template_id
                  ? errors?.custom_template_id?.message
                  : "template id"}
              </label>
              <div className="relative mt-2">
                <input
                  placeholder="e.g. 9859"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={
                    disabled || instoreTypesLoading || instoreOrderTypesLoading
                  }
                  type="number"
                  id="custom_template_id"
                  {...register("custom_template_id")}
                />
              </div>
            </fieldset>

            {/* instore_type */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="instore_type"
                className={
                  errors.instore_type
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.instore_type
                  ? errors?.instore_type?.message
                  : "instore type"}
              </label>
              <div className="relative mt-2">
                <select
                  id="function"
                  defaultValue="Retail"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={
                    disabled || instoreTypesLoading || instoreOrderTypesLoading
                  }
                  {...register("instore_type", {
                    required: "This is a required field",
                  })}
                >
                  {instoreTypes_Loaded &&
                    instoreTypes?.map((type: InstoreType) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </div>
            </fieldset>

            {/* order_type_instore_name */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="instore_type"
                className={
                  errors.order_type_instore_name
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.order_type_instore_name
                  ? errors?.order_type_instore_name?.message
                  : "order type"}
              </label>
              <div className="relative mt-2">
                <select
                  id="function"
                  defaultValue="Opening"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={
                    disabled || instoreTypesLoading || instoreOrderTypesLoading
                  }
                  {...register("order_type_instore_name", {
                    required: "This is a required field",
                  })}
                >
                  {instoreOrderTypes_Loaded &&
                    instoreOrderTypes?.map((type: InstoreOrderType) => (
                      <option key={type.id} value={type.name}>
                        {type.name} - {type.short_name}
                      </option>
                    ))}
                </select>
              </div>
            </fieldset>

            {/* from */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="from"
                className={
                  errors.from
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.from ? errors?.from?.message : "from date"}
              </label>
              <div className="relative mt-2">
                <input
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={
                    disabled || instoreTypesLoading || instoreOrderTypesLoading
                  }
                  type="date"
                  id="from"
                  {...register("from", {
                    required: "This is a required field",
                  })}
                />
              </div>
            </fieldset>

            {/* until */}
            <fieldset className="sm:col-span-3">
              <label
                htmlFor="until"
                className={
                  errors.until
                    ? "block text-sm font-medium leading-6 text-red-900"
                    : "block text-sm font-medium leading-6 text-gray-900"
                }
              >
                {errors.until ? errors?.until?.message : "until date"}
              </label>
              <div className="relative mt-2">
                <input
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6"
                  disabled={
                    disabled || instoreTypesLoading || instoreOrderTypesLoading
                  }
                  type="date"
                  {...register("until", {
                    required: "This is a required field",
                  })}
                />
              </div>
            </fieldset>

            {/* active */}
            <fieldset className="sm:col-span-6">
              <div className="relative flex gap-x-3">
                <div className="flex h-6 items-center">
                  <input
                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-600"
                    disabled={
                      disabled ||
                      instoreTypesLoading ||
                      instoreOrderTypesLoading
                    }
                    type="checkbox"
                    {...register("active")}
                  />
                </div>
                <div className="text-sm leading-6">
                  <label htmlFor="active" className="font-medium text-gray-900">
                    active
                  </label>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          onClick={cancelCUD}
          type="button"
          disabled={disabled || instoreTypesLoading || instoreOrderTypesLoading}
          className={classNames(
            disabled
              ? "text-gray-300 hover:cursor-not-allowed"
              : "text-rose-700",
            "text-sm font-semibold leading-6"
          )}
        >
          Cancel
        </button>

        <SubmitButton
          conditionals={[isValid]}
          isSubmitting={isSubmitting}
          disabled={disabled || instoreTypesLoading || instoreOrderTypesLoading}
        />
      </div>
    </form>
  );
}
